const BASE = '';

async function req(method, path, body, isForm) {
  const opts = {
    method,
    credentials: 'include',
    headers: isForm ? {} : { 'Content-Type': 'application/json' },
    body: isForm ? body : (body ? JSON.stringify(body) : undefined),
  };
  const res = await fetch(BASE + path, opts);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'Request failed');
  }
  return res.json();
}

export const api = {
  // Auth
  login:      (email, password) => req('POST', '/api/auth/login', { email, password }),
  logout:     ()                 => req('POST', '/api/auth/logout'),
  me:         ()                 => req('GET',  '/api/auth/me'),

  // Transactions
  getTxs:     (year, month, showHidden) =>
    req('GET', `/api/transactions?year=${year}&month=${month}&show_hidden=${showHidden||false}`),
  // All months for one envelope — the card is all-time, so its filtered list must
  // ignore the selected month. Always fetches hidden too; the client splits them
  // so the "Show hidden" toggle still works within the set.
  getTxsByEnvelope: (envelopeId) =>
    req('GET', `/api/transactions?envelope_id=${envelopeId}&show_hidden=true`),
  updateTx:   (id, data)        => req('PATCH', `/api/transactions/${id}`, data),
  confirmAll: (year, month)     => req('POST', '/api/transactions/confirm-all', { year, month }),

  // Summary
  getSummary: (year, month)     => req('GET', `/api/summary?year=${year}&month=${month}`),
  getMedical: ()                 => req('GET', '/api/medical'),
  getTrends:  (months)           => req('GET', `/api/trends?months=${months||6}`),

  // Rules
  getRules:   ()                 => req('GET',    '/api/rules'),
  addRule:    (keyword, category, view)=> req('POST', '/api/rules', { keyword, category, view }),
  deleteRule: (id)               => req('DELETE', `/api/rules/${id}`),

  // Accounts
  getAccounts:   ()         => req('GET',    '/api/accounts'),
  // "As of" a given date — see server/index.js for why this must be a
  // separate call from getAccounts()'s global last_transaction_date.
  getAccountActivity: (before) => req('GET', `/api/accounts/last-activity?before=${before}`),
  addAccount:    (data)     => req('POST',   '/api/accounts', data),
  updateAccount: (id, data) => req('PATCH',  `/api/accounts/${id}`, data),
  deleteAccount: (id)       => req('DELETE', `/api/accounts/${id}`),

  // Envelopes — funded spending pots. spent/tx_count/first_tagged/last_tagged are
  // derived server-side across all time, so the card reads them here rather than
  // recomputing from the month's transactions.
  getEnvelopes:   ()         => req('GET',    '/api/envelopes'),
  addEnvelope:    (data)     => req('POST',   '/api/envelopes', data),
  updateEnvelope: (id, data) => req('PATCH',  `/api/envelopes/${id}`, data),
  deleteEnvelope: (id)       => req('DELETE', `/api/envelopes/${id}`),

  // Categories
  getCats:    ()                 => req('GET',    '/api/categories'),
  addCat:     (data)             => req('POST',   '/api/categories', data),
  updateCat:  (id, data)         => req('PATCH',  `/api/categories/${id}`, data),
  deleteCat:  (id)               => req('DELETE', `/api/categories/${id}`),

  // Settings — budget target percentages (needs/wants/savings)
  getSettings:  ()     => req('GET',   '/api/settings'),
  saveSettings: (data) => req('PATCH', '/api/settings', data),

  // Upload errors
  getUploadErrors:   ()   => req('GET',    '/api/upload-errors'),
  deleteUploadError: (id) => req('DELETE', `/api/upload-errors/${id}`),
  clearUploadErrors: ()   => req('DELETE', '/api/upload-errors'),

  // Import log
  getImportLog:     (page) => req('GET',    `/api/import-log?page=${page||1}&limit=10`),
  deleteImportBatch: (batchId) => req('DELETE', `/api/import-log/${batchId}`),

  // Upload
  previewUpload: (files) => {
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    return req('POST', '/api/upload/preview', fd, true);
  },

  upload: (files, overrides, excludedIds, forceImport) => {
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    fd.append('overrides', JSON.stringify(overrides || []));
    fd.append('excluded_ids', JSON.stringify(excludedIds || []));
    fd.append('force_import', JSON.stringify(forceImport || []));
    return req('POST', '/api/upload', fd, true);
  },

  // Investments — a separate path from the statement upload above. Same
  // two-step shape (preview writes nothing, import commits), but the question
  // the modal asks is the snapshot date rather than the account.
  previewInvestments: (files) => {
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    return req('POST', '/api/investments/preview', fd, true);
  },

  importInvestments: (files, snapshotDate) => {
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    fd.append('snapshot_date', snapshotDate || '');
    return req('POST', '/api/investments/import', fd, true);
  },

  getInvestments: () => req('GET', '/api/investments/data'),

  // AUM — assets minus debt, across bank balances, Questrade equity and manual
  // holdings. as_of defaults to today server-side; the series comes back in the
  // same payload so a total and its coverage caveat can never disagree.
  getAum: (view, asOf) =>
    req('GET', `/api/aum?view=${view || 'household'}${asOf ? `&as_of=${asOf}` : ''}`),

  // Manual holdings — money with no export behind it. Every value change goes
  // through PATCH with an explicit event_type, so the reason is never inferred.
  getManualHoldings: ()   => req('GET',    '/api/manual-holdings'),
  getManualEvents:   (id) => req('GET',    `/api/manual-holdings/${id}/events`),
  addManualHolding:  (d)  => req('POST',   '/api/manual-holdings', d),
  updateManualHolding: (id, d) => req('PATCH', `/api/manual-holdings/${id}`, d),
  closeManualHolding:  (id, d) => req('POST',  `/api/manual-holdings/${id}/close`, d),
  deleteManualHolding: (id)    => req('DELETE',`/api/manual-holdings/${id}`),

  // Monthly analysis — findings are computed client-side; these two just store
  // and list snapshots for the History section of the card.
  saveMonthlyAnalysis: (year, month, view, findings) =>
    req('POST', '/api/monthly-analysis', { year, month, view, findings }),
  getMonthlyAnalysisHistory: (view, page) =>
    req('GET', `/api/monthly-analysis?view=${view}&page=${page||1}&limit=12`),

  // Export
  exportCSV: (year, month) => {
    window.location.href = `/api/export/csv?year=${year}&month=${month}`;
  },
};
